import type { PropsOf } from '../utils'

import { omitThemingProps, styled, useStyleConfig, vui } from '../core'
import Icon from '../icon'

// ---------------------------------------------------------------------------
// DismissButton — Standalone icon-only dismiss action
//
// Optimized for compact, icon-only dismiss affordances in alerts, tags, dialogs.
// Sizes: xs (20), sm (24), md (32), lg (40) — icon always 16px, centered with padding.
// Simplified component: no Button inheritance, no variants, just dismiss styling.
// ---------------------------------------------------------------------------

type DismissButtonSize = 'xs' | 'sm' | 'md' | 'lg'

const DismissButtonBase = styled.buttonBox`
  align-items: center;
  appearance: none;
  background: transparent;
  border: none;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  outline: none;
  padding: 0;
  position: relative;

  /* Inherit dismiss color from parent (Tag, Alert, etc) via CSS custom property */
  --vui-dismiss-color: var(--vui-dismiss-fg, var(--vui-feedback-neutral-solid));
  color: var(--vui-dismiss-text, var(--vui-dismiss-color));

  /* Hover: grey solid hover @ 20% opacity */
  &:hover {
    background-color: color-mix(in srgb, var(--vui-action-grey-solid-hover) 20%, transparent);
  }

  /* Active/pressed: grey solid pressed @ 10% opacity */
  &:active {
    background-color: color-mix(in srgb, var(--vui-action-grey-solid-pressed) 10%, transparent);
  }

  /* Disabled state */
  &:disabled {
    color: var(--vui-action-disabled-foreground);
    cursor: not-allowed;
    opacity: 0.5;
  }

  /* Focus visible (keyboard) */
  &:focus-visible {
    outline: 2px solid var(--vui-control-focus-ring, #00a1db);
    outline-offset: 2px;
  }

  transition: background-color 150ms ease-out;
`

/**
 * DismissButton — Icon-only close/dismiss affordance
 *
 * Standalone button component for dismiss actions in compact layouts.
 * Uses currentColor for icon, controlled via CSS custom property --vui-dismiss-fg.
 *
 * @example
 * // Default (md): 32×32
 * <DismissButton onClick={onDismiss} aria-label="Close" />
 *
 * // Small (sm): 24×24 for tags
 * <DismissButton size="sm" onClick={onDismiss} aria-label="Dismiss" />
 *
 * // With color inheritance from parent
 * <div style={{ '--vui-dismiss-fg': '#fff' }}>
 *   <DismissButton size="lg" />
 * </div>
 */
export type DismissButtonProps = PropsOf<typeof DismissButtonBase, { size?: DismissButtonSize }>

export const DismissButton = vui<typeof DismissButtonBase, { size?: DismissButtonSize }>((props, ref) => {
  const rest = omitThemingProps(props)
  const styles = useStyleConfig('DismissButton', props)

  return (
    <DismissButtonBase ref={ref} type="button" aria-label="Dismiss" {...styles} {...rest}>
      <Icon name="uiTimes" h={16} w={16} />
    </DismissButtonBase>
  )
})

DismissButton.displayName = 'DismissButton'

export type { DismissButtonSize }
